import { ref, computed, watch } from 'vue'
import { io } from 'socket.io-client'
import { useAuthStore } from '~/stores/useAuthStore'

// Singleton state - compartido entre todas las instancias
let socketInstance = null
let lastToken = null
let watchersBound = false
const isConnected = ref(false)
const activeChannels = ref(new Set())
const notificationCallbacks = new Set()
const messageCreatedCallbacks = new Set()
const messageUpdatedCallbacks = new Set()
const messageDeletedCallbacks = new Set()
const notificationNewCallbacks = new Set()
const reloadCallbacks = new Map()
/** Debounce de dispatch de reloads (fix F16): clave `contexto:promotionId` → timer */
const reloadTimers = new Map()

/**
 * Composable para gestionar conexiones WebSocket con Socket.io
 * Usa un patrón singleton para mantener una única conexión
 */
export const useSocket = () => {
  const config = useRuntimeConfig()
  const authStore = useAuthStore()

  // URL del WebSocket (fallback a apiBase)
  const wsUrl = computed(() => {
    return config.public.wsUrl || config.public.apiBase
  })

  // Reconectar con un nuevo token
  const reconnect = () => {
    if (!socketInstance) return

    // Desconectar socket actual y eliminar referencia
    socketInstance.disconnect()
    socketInstance = null

    // Conectar con nuevo token; los canales se restaurarán en el handler 'connect'
    connect()
  }

  // Conectar socket
  const connect = () => {
    if (socketInstance) {
      // Ya existe una instancia; solo reconectar si está desconectada
      if (!socketInstance.connected) {
        socketInstance.connect()
      }
      return
    }
    if (!authStore.accessToken) return

    lastToken = authStore.accessToken

    socketInstance = io(wsUrl.value, {
      auth: {
        token: authStore.accessToken
      },
      transports: ['websocket', 'polling'],
      // Fallback real a polling cuando el handshake ws falla: engine.io-client
      // solo prueba el siguiente transporte con esta flag (por defecto, con
      // websocket primero, un fallo de ws mata la conexión sin probar polling)
      tryAllTransports: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000
    })

    // Eventos de conexión
    socketInstance.on('connect', () => {
      isConnected.value = true
      // Unirse al canal de usuario automáticamente
      const userId = authStore.userId
      if (userId) {
        activeChannels.value.add(`user-${userId}`)
      }
      // Unirse al canal de empresa automáticamente
      const companyId = authStore.companyId
      if (companyId) {
        activeChannels.value.add(`company-${companyId}`)
      }
      // Re-unirse a todos los canales activos (útil tras reconexión automática)
      activeChannels.value.forEach(channel => {
        socketInstance.emit('join', { channel })
      })
    })

    socketInstance.on('disconnect', () => {
      isConnected.value = false
      // No limpiar canales en disconnect, puede ser temporal
    })

    socketInstance.on('connect_error', (error) => {
      console.error('[Socket] Connection error:', error.message)
      // Si es error de autenticación, el watch del token se encargará de reconectar
    })

    // Escuchar notificaciones y propagar a todos los callbacks
    socketInstance.on('notification', (data) => {
      notificationCallbacks.forEach(callback => callback(data))
    })

    // Escuchar eventos específicos de mensajes del API
    socketInstance.on('message_created', (payload) => {
      messageCreatedCallbacks.forEach(callback => callback(payload))
    })

    socketInstance.on('message_updated', (payload) => {
      messageUpdatedCallbacks.forEach(callback => callback(payload))
    })

    socketInstance.on('message_deleted', (payload) => {
      messageDeletedCallbacks.forEach(callback => callback(payload))
    })

    // Escuchar notificaciones nuevas
    socketInstance.on('notification:new', (payload) => {
      notificationNewCallbacks.forEach(callback => callback(payload))
    })

    // Escuchar eventos genéricos de recarga de datos
    socketInstance.on('reload', (payload) => {
      if (!payload || payload.action !== 'reload') return
      // Ignorar recargas originadas por el propio usuario
      if (payload.triggeredBy && payload.triggeredBy === authStore.userId) return

      const contextCallbacks = reloadCallbacks.get(payload.context)
      if (!contextCallbacks) return

      // Debounce por contexto + promoción: un mismo cambio emite ráfagas de
      // reloads (p. ej. el apply de una simulación, N+2) y sin esto cada uno
      // lanza un refetch completo, desordenable (fix F16). Gana el último
      // scope de la ráfaga.
      const key = `${payload.context}:${payload.scope?.promotionId ?? ''}`
      const pending = reloadTimers.get(key)
      if (pending) clearTimeout(pending)

      reloadTimers.set(key, setTimeout(() => {
        reloadTimers.delete(key)
        contextCallbacks.forEach(({ callback, filter }) => {
          if (filter && !filter(payload.scope)) return
          callback(payload.scope)
        })
      }, 300))
    })
  }

  // Desconectar socket completamente
  const disconnect = () => {
    if (socketInstance) {
      socketInstance.disconnect()
      socketInstance = null
    }
    isConnected.value = false
    activeChannels.value.clear()
    notificationCallbacks.clear()
    messageCreatedCallbacks.clear()
    messageUpdatedCallbacks.clear()
    messageDeletedCallbacks.clear()
    notificationNewCallbacks.clear()
    reloadCallbacks.clear()
    // Sin logout no deben dispararse dispatch de reloads aplazados (fix F16)
    reloadTimers.forEach(timer => clearTimeout(timer))
    reloadTimers.clear()
    lastToken = null
  }

  // Interno: unirse a un canal
  const _joinChannel = (subject, subjectId) => {
    if (!socketInstance?.connected) {
      console.warn('[Socket] Cannot join channel: socket not connected')
      return
    }

    const channel = `${subject}-${subjectId}`
    socketInstance.emit('join', { channel })
    activeChannels.value.add(channel)
  }

  // Unirse a un canal (público) - espera conexión si es necesario
  const joinChannel = (subject, subjectId) => {
    if (!socketInstance?.connected) {
      // Si no está conectado, esperar a que lo esté
      const unwatch = watch(isConnected, (connected) => {
        if (connected) {
          _joinChannel(subject, subjectId)
          unwatch()
        }
      })
      return
    }
    _joinChannel(subject, subjectId)
  }

  // Interno: salir de un canal
  const _leaveChannel = (subject, subjectId) => {
    const channel = `${subject}-${subjectId}`
    activeChannels.value.delete(channel)

    if (!socketInstance?.connected) {
      console.warn('[Socket] Cannot leave channel: socket not connected')
      return
    }

    socketInstance.emit('leave', { channel })
  }

  // Salir de un canal (público)
  const leaveChannel = (subject, subjectId) => {
    _leaveChannel(subject, subjectId)
  }

  // Suscribirse a notificaciones
  // Retorna función para cancelar suscripción
  const onNotification = (callback) => {
    notificationCallbacks.add(callback)

    // Retornar función para cancelar suscripción
    return () => {
      notificationCallbacks.delete(callback)
    }
  }

  // Suscribirse a eventos de mensajes creados
  const onMessageCreated = (callback) => {
    messageCreatedCallbacks.add(callback)
    return () => {
      messageCreatedCallbacks.delete(callback)
    }
  }

  // Suscribirse a eventos de mensajes actualizados
  const onMessageUpdated = (callback) => {
    messageUpdatedCallbacks.add(callback)
    return () => {
      messageUpdatedCallbacks.delete(callback)
    }
  }

  // Suscribirse a eventos de mensajes eliminados
  const onMessageDeleted = (callback) => {
    messageDeletedCallbacks.add(callback)
    return () => {
      messageDeletedCallbacks.delete(callback)
    }
  }

  // Suscribirse a notificaciones nuevas
  const onNotificationNew = (callback) => {
    notificationNewCallbacks.add(callback)
    return () => {
      notificationNewCallbacks.delete(callback)
    }
  }

  // Suscribirse a eventos genéricos de recarga
  // filter: función opcional (scope) => boolean para filtrar por scope
  const onReload = (context, callback, filter = null) => {
    if (!reloadCallbacks.has(context)) {
      reloadCallbacks.set(context, new Set())
    }
    const subscription = { callback, filter }
    reloadCallbacks.get(context).add(subscription)

    return () => {
      reloadCallbacks.get(context)?.delete(subscription)
    }
  }

  // Cancelar suscripción a eventos de recarga
  const offReload = (context, callback) => {
    const contextCallbacks = reloadCallbacks.get(context)
    if (!contextCallbacks) return
    for (const subscription of contextCallbacks) {
      if (subscription.callback === callback) {
        contextCallbacks.delete(subscription)
        return
      }
    }
  }

  // Watchers de autenticación: se crean UNA sola vez a nivel de módulo. Antes
  // vivían en cada llamada a useSocket(), así que cada consumidor (páginas,
  // usePlanningAlert a nivel módulo…) añadía su par de watchers: en cada
  // refresh de token se evaluaban todos y se disparaban N callbacks de
  // connect/disconnect/reconnect sobre el singleton (fix F15). Los watchers
  // sobreviven al logout a propósito: reconectan en el próximo login.
  if (!watchersBound) {
    watchersBound = true

    watch(
      () => authStore.isAuthenticated,
      (isAuth) => {
        if (isAuth) {
          connect()
        } else {
          disconnect()
        }
      },
      { immediate: true }
    )

    watch(
      () => authStore.accessToken,
      (newToken) => {
        // Solo reconectar si el token realmente cambió y hay usuario autenticado
        if (newToken && authStore.isAuthenticated && newToken !== lastToken) {
          reconnect()
        }
      }
    )
  }

  return {
    socket: socketInstance,
    isConnected,
    activeChannels,
    connect,
    disconnect,
    joinChannel,
    leaveChannel,
    onNotification,
    onMessageCreated,
    onMessageUpdated,
    onMessageDeleted,
    onNotificationNew,
    onReload,
    offReload
  }
}
